import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import jwt_decode from "jwt-decode";
import moment from "moment";
import { Config } from "../Config";
import { defaultLimit, defaultOffset } from "./Constatnts";
import { AuthenticationError, AuthorizationError, EntityNotFoundError, NetworkError, ValidationError } from "./Errors";
import {
  BlockReasonTypes,
  Brand,
  ChangeLogResponse,
  CredentialResponse,
  EncodedFields,
  PendingActions,
  PredefinedQuestion,
  Question,
  QuestionAnswer,
  UpdateUserCredentialsRequest,
  UserActivityResponse,
  UserPermissions,
} from "./Types";

export const errorInterceptor = (error: any) => {
  const { response } = error;
  
  if(!response?.status) {
    throw new NetworkError("Network Error");
  }

  if (response.status === 422) {
    throw new ValidationError(
      "Validation",
      response.data.reasons.map((reason: any) => reason.message),
      response.config.data
    );
  }

  if(response.status === 401){
    throw new AuthenticationError("Unauthenticated");
  }

  if(response.status === 403){
    
    throw new AuthorizationError("Unauthorized");
  }

  if (response.status === 404) {
    throw new EntityNotFoundError("Entity not found", response.config.data);
  }

  if(response.status === 500 && error.config.url === "/me/brand"){
    return {};
  }

  throw new Error(error);
};

export default class UserCredentialsApi {
  private axiosPrivateInstance!: AxiosInstance;
  private axiosPublicInstance!: AxiosInstance;
  private signInSilent?: Function;
  private token!: string;

  constructor() {}

  async init(token: string | undefined, signInSilent: Function, originClient?: string | null) {
    
    this.axiosPrivateInstance = axios.create({
      baseURL: Config.app.apiUnifiedCredentialsUrl,
    });

    this.axiosPublicInstance = axios.create({
      baseURL: Config.app.apiPublicUnifiedCredentialsUrl,
    });

    const responseInterceptor = (resp: AxiosResponse) => {
      return resp.data;
    };

    this.signInSilent = signInSilent;    
    this.token = token ? token : "";

    const requestInterceptor = async (config: AxiosRequestConfig) => {
      if (!this.signInSilent) {
        throw new Error("signInSilent is not set");
      }
      if (!this.token && this.token === "") {
        this.token = await this.signInSilent();
      }  
      if (this.shouldRefreshToken()) {
        this.token = await this.signInSilent();
      }
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${this.token}`,
      };
      if (originClient) {
        config.headers["X-Origin-Client"] = originClient;
      }
      return config;
    }

    this.axiosPrivateInstance.interceptors.request.use(requestInterceptor);

    this.axiosPrivateInstance.interceptors.response.use(
      responseInterceptor,
      errorInterceptor
    );

    this.axiosPublicInstance.interceptors.response.use(
      responseInterceptor,
      errorInterceptor
    );
  }

  shouldRefreshToken(): boolean {
    if (this.token && this.token !== "") {
      const decodedToken: any = jwt_decode(this.token);
      const tokenExpiration = moment
        .unix(decodedToken.exp)
        .subtract(10, "seconds");
      return moment().isSameOrAfter(tokenExpiration);
    } else {
      return true;
    }
  }

  getToken() {
    return this.token;
  }

  async getCredentialsById(credentialId: string): Promise<CredentialResponse> {
    return await this.axiosPrivateInstance.post(
      `/credentials/searches/by-credential-id?expand=questions,blocks,profiles,auth0Identifiers,mfaFactors`,
      {
        id: credentialId,
      }
    );
  }

  async getCredentialsStatus(credentialId: string): Promise<PendingActions> {
    return await this.axiosPrivateInstance.post(
      `/credentials/pending-actions-by-scopes`,
      {
        id: credentialId,
      }
    );
  }

  async sendEmailVerificationEmail(id: string): Promise<any> {
    return await this.axiosPrivateInstance.post(
      `/credentials/emails/initiate-email-verification`,
      {
        id,
      }
    );
  }

  async setTemporaryPassword(id: string): Promise<any> {
    return await this.axiosPrivateInstance.patch(
      `/credentials/set-temporary-password`,
      {
        id,
      }
    );
  }

  async sendResetPasswordEmail(id: string): Promise<any> {
    return await this.axiosPrivateInstance.post(
      `/credentials/emails/send-password-reset`,
      {
        id,
      }
    );
  }

  async unblockUser(credentialId: string): Promise<any> {
    return await this.axiosPrivateInstance.patch(`/credentials/blocked`, {
      credentialId,
      blocked: false,
    });
  }

  async resetSecurityQuestion(id: string): Promise<any> {
    return await this.axiosPrivateInstance.patch(
      `/credentials/sqa-force-reset-by-scopes`,
      {
        id,
      }
    );
  }

  async verifyEmail(id: string): Promise<any> {
    return await this.axiosPrivateInstance.patch(
      "/credentials/check-email-verified",
      {
        id,
      }
    );
  }

  async updateUserCredentials(
    updateUserCredentialsRequest: UpdateUserCredentialsRequest,
    endodedFields?: EncodedFields[]
  ): Promise<CredentialResponse> {
    return await this.axiosPrivateInstance.patch(
      "/update-credentials?expand=questions",
      {
        ...updateUserCredentialsRequest,
      },
      {
        headers: {
          "x-encoded-fields": endodedFields?.join(",") || ""
        }
      }
    );
  }

  async updateUserCredentialsWithId(
    credentialId: string,
    updateUserCredentialsRequest: UpdateUserCredentialsRequest
  ): Promise<CredentialResponse> {
    return await this.axiosPrivateInstance.patch("/update-credentials", {
      ...updateUserCredentialsRequest,
      id: credentialId,
    });
  }

  async resetPhoneNumber(id: string): Promise<any> {
    return await this.axiosPrivateInstance.patch(
      "/credentials/phone-force-reset",
      {
        id,
      }
    );
  }

  async getQuestionAnswersById(id: string): Promise<QuestionAnswer> {
    return await this.axiosPrivateInstance.post(
      "/question-answers/searches/by-id-by-scopes",
      {
        id,
      }
    );
  }

  async getQuestionAnswersByCredentialsId(
    credentialId: string
  ): Promise<Question> {
    return await this.axiosPrivateInstance.post(
      "/question-answers/searches/by-credential-id-by-scopes",
      {
        credentialId,
      }
    );
  }

  async getQuestions(): Promise<PredefinedQuestion[]> {
    return ((await this.axiosPublicInstance.get("/questions")) as any).items;
  }

  async unblockUserCredentials(
    credentialId: string,
    type: BlockReasonTypes
  ): Promise<any> {
    return await this.axiosPrivateInstance.delete("/credential-blocks-by-scopes", {
      data: {
        credentialId,
        type,
      },
    });
  }

  async blockUserCredentials(
    credentialId: string,
    type: BlockReasonTypes
  ): Promise<any> {
    return await this.axiosPrivateInstance.post("/credential-blocks-by-scopes", {
      credentialId,
      type,
    });
  }

  async getUserActivity(
    credentialId: string,
    offset: number = defaultOffset,
    limit: number = defaultLimit
  ): Promise<UserActivityResponse> {
    return (await this.axiosPrivateInstance.post(
      `/activity-logs/searches/by-credential-id-by-scopes`,
      {
        credentialId,
      },
      {
        params: {
          offset,
          limit,
        },
      }
    )) as any;
  }

  async getChangeLogs(
    credentialId: string,
    offset: number = defaultOffset,
    limit: number = defaultLimit
  ): Promise<ChangeLogResponse> {
    return (await this.axiosPrivateInstance.post(
      `/change-logs/searches/by-credential-id-by-scopes`,
      {
        credentialId,
      },
      {
        params: {
          offset,
          limit,
        },
      }
    )) as any;
  }

  async getPermissions(): Promise<Array<UserPermissions>> {
    return ((await this.axiosPrivateInstance.get("/me/permissions-by-scopes")) as any)
      .items;
  }

  async getPermissionsWithIdToken(idToken: string): Promise<Array<UserPermissions>> {
    return ((await this.axiosPrivateInstance.post("/me/permissions-by-scopes", { idToken })) as any)
      .items;
  }

  async getBranding(): Promise<Brand> {
    return (await this.axiosPrivateInstance.get("/me/brand")) as any;
  }
}

// await new Promise((res) => setTimeout(res, 1000));
